import {
  ALLOWED_COST_DOCUMENT_TYPES,
  ApiError,
  createOutgoingPaymentSchema,
  formatMoney,
  MANUAL_PAYMENT_METHODS,
  MAX_COST_DOCUMENT_BYTES,
  OUTGOING_PAYMENT_KIND_LABELS,
  OUTGOING_PAYMENT_KINDS,
  OUTGOING_PAYMENT_METHOD_LABELS,
  type OutgoingPayment,
  type OutgoingPaymentKind,
  type OutgoingPaymentMethod,
  type Payee,
  type PurchaseOrderListItem,
  queryKeys,
  type Vendor,
} from '@mevaser/shared'
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api.js'

const ACCEPT = ALLOWED_COST_DOCUMENT_TYPES.join(',')
const NONE = '__none__'

export interface PaymentPrefill {
  kind?: OutgoingPaymentKind
  vendorId?: string
  purchaseOrderId?: string
  staffUserId?: string
  payeeId?: string
  /** Pre-fills the amount (whole cents) — e.g. pay worked out from the time clock. */
  amountCents?: number
  note?: string
}

interface StaffOption {
  id: string
  name: string
}
interface Category {
  id: string
  name: string
  isActive: boolean
}

const today = () => new Date().toLocaleDateString('en-CA')

/** Dollars as typed ("$3,749.50", "3749.5") → whole cents; NaN if it isn't a number. */
const toCents = (text: string) => {
  const clean = text.replace(/[$,\s]/g, '')
  return clean === '' ? 0 : Math.round(Number(clean) * 100)
}

/** The lists the payment pickers draw from, shared by every dialog that records one. */
export function usePaymentLookups(enabled: boolean) {
  const vendors = useQuery({
    queryKey: queryKeys.vendors.list({ picker: true }),
    queryFn: () =>
      api.get<{ items: Vendor[] }>('/vendors', { query: { pageSize: 200, isActive: 'true' } }),
    enabled,
  })
  const staff = useQuery({
    queryKey: ['expenses', 'staff-picker'],
    queryFn: () => api.get<StaffOption[]>('/staff'),
    enabled,
  })
  const payees = useQuery({
    queryKey: queryKeys.expenses.payees(),
    queryFn: () => api.get<{ items: Payee[] }>('/payees'),
    enabled,
  })
  const categories = useQuery({
    queryKey: queryKeys.expenses.categories(),
    queryFn: () => api.get<{ items: Category[] }>('/expense-categories'),
    enabled,
  })
  return {
    vendors: vendors.data?.items ?? [],
    staff: staff.data ?? [],
    payees: (payees.data?.items ?? []).filter((p) => p.isActive),
    categories: (categories.data?.items ?? []).filter((c) => c.isActive),
  }
}

function PickOne<T extends { id: string }>({
  label,
  items,
  value,
  onChange,
  name,
  placeholder,
  error,
}: {
  label: string
  items: T[]
  value: string
  onChange: (id: string) => void
  name: (item: T) => string
  placeholder: string
  error?: string
}) {
  return (
    <Field label={label} required error={error}>
      {(props) => (
        <Select value={value || undefined} onValueChange={onChange}>
          <SelectTrigger {...props} invalid={Boolean(error)}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {items.map((i) => (
              <SelectItem key={i.id} value={i.id}>
                {name(i)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}

/**
 * Record money going out (t1164): a payment already made, or a bill that is
 * due. Shared by the Payments page, the vendor page and a purchase order, each
 * passing what it already knows as `prefill`. Nothing is sent from here — the
 * method and reference record how it moved at the bank.
 */
export function RecordPaymentDialog({
  open,
  onClose,
  prefill,
}: {
  open: boolean
  onClose: () => void
  prefill?: PaymentPrefill
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const lookups = usePaymentLookups(open)

  const [kind, setKind] = useState<OutgoingPaymentKind>('vendor')
  const [vendorId, setVendorId] = useState('')
  const [staffUserId, setStaffUserId] = useState('')
  const [payeeId, setPayeeId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [payeeLabel, setPayeeLabel] = useState('')
  const [orderId, setOrderId] = useState('')
  const [invoiceRef, setInvoiceRef] = useState('')
  const [markPaid, setMarkPaid] = useState(false)
  const [paySupplies, setPaySupplies] = useState(false)
  /** The order whose office supplies were already offered, so the default is applied once. */
  const suppliesOfferedFor = useRef('')
  const [amount, setAmount] = useState('')
  const [alreadyPaid, setAlreadyPaid] = useState(true)
  const [method, setMethod] = useState<OutgoingPaymentMethod>('ach')
  const [paidOn, setPaidOn] = useState(today())
  const [dueOn, setDueOn] = useState('')
  const [reference, setReference] = useState('')
  const [note, setNote] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!open) return
    setFormError(null)
    setFieldErrors({})
    setKind(prefill?.kind ?? (prefill?.vendorId ? 'vendor' : 'vendor'))
    setVendorId(prefill?.vendorId ?? '')
    setStaffUserId(prefill?.staffUserId ?? '')
    setPayeeId(prefill?.payeeId ?? '')
    setCategoryId('')
    setPayeeLabel('')
    setOrderId(prefill?.purchaseOrderId ?? '')
    setInvoiceRef('')
    setMarkPaid(false)
    setPaySupplies(false)
    suppliesOfferedFor.current = ''
    setAmount(prefill?.amountCents ? (prefill.amountCents / 100).toFixed(2) : '')
    setAlreadyPaid(true)
    setMethod('ach')
    setPaidOn(today())
    setDueOn('')
    setReference('')
    setNote(prefill?.note ?? '')
    setFile(null)
  }, [open, prefill])

  const vendor = lookups.vendors.find((v) => v.id === vendorId)

  // A vendor's orders still waiting on payment, so a payment can be tied to one.
  const orders = useQuery({
    queryKey: queryKeys.purchaseOrders.list({ forPayment: vendor?.name ?? null }),
    queryFn: () =>
      api.get<{ items: PurchaseOrderListItem[] }>('/purchase-orders', {
        query: { q: vendor?.name, pageSize: 100 },
      }),
    enabled: open && kind === 'vendor' && Boolean(vendor),
  })
  const vendorOrders = (orders.data?.items ?? []).filter(
    (o) =>
      o.vendor.trim().toLowerCase() === vendor?.name.trim().toLowerCase() &&
      (o.paymentStatus !== 'paid' || o.id === orderId),
  )

  const balance = useQuery({
    queryKey: queryKeys.expenses.balance(orderId || 'none'),
    queryFn: () =>
      api.get<{
        purchaseOrderId: string
        poNumber: string
        totalCents: number
        paidCents: number
        balanceCents: number
        invoiceExpenses: { id: string; number: string; amountCents: number; status: string }[]
      }>(`/outgoing-payments/order-balance/${orderId}`),
    enabled: open && Boolean(orderId),
  })

  const amountCents = toCents(amount)
  const covers = Boolean(balance.data) && amountCents >= (balance.data?.balanceCents ?? 0)
  const linkable = kind === 'vendor' && (Boolean(orderId) || invoiceRef.trim() !== '')

  // t1223: office supplies entered with this order's invoice and still owed — one
  // payment can settle them together with the order.
  const unpaidSupplies = (balance.data?.invoiceExpenses ?? []).filter((e) => e.status === 'unpaid')
  const suppliesCents = unpaidSupplies.reduce((sum, e) => sum + e.amountCents, 0)
  useEffect(() => {
    const data = balance.data
    if (!data || suppliesCents === 0 || suppliesOfferedFor.current === data.purchaseOrderId) return
    suppliesOfferedFor.current = data.purchaseOrderId
    // Only on by default when the amount is the order's balance (or still empty), so
    // adding the supplies to it cannot surprise anyone who typed their own figure.
    if (alreadyPaid && (amount.trim() === '' || toCents(amount) === data.balanceCents)) {
      setPaySupplies(true)
      setAmount(((data.balanceCents + suppliesCents) / 100).toFixed(2))
    }
  }, [balance.data, suppliesCents, alreadyPaid, amount])
  const payingSupplies = alreadyPaid && Boolean(orderId) && paySupplies && suppliesCents > 0
  const toggleSupplies = (on: boolean) => {
    setPaySupplies(on)
    setAmount((current) => {
      const cents = toCents(current)
      if (!Number.isFinite(cents)) return current
      return (Math.max(0, cents + (on ? suppliesCents : -suppliesCents)) / 100).toFixed(2)
    })
  }

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        kind,
        vendorId: kind === 'vendor' || (kind === 'expense' && vendorId) ? vendorId || null : null,
        staffUserId: kind === 'staff' ? staffUserId : null,
        payeeId: kind === 'payee' || (kind === 'expense' && payeeId) ? payeeId || null : null,
        payeeLabel: kind === 'expense' ? payeeLabel.trim() || null : null,
        categoryId: kind === 'expense' ? categoryId : null,
        purchaseOrderId: kind === 'vendor' && orderId ? orderId : null,
        invoiceRef: kind === 'vendor' && !orderId ? invoiceRef.trim() || null : null,
        amountCents,
        method: alreadyPaid ? method : null,
        paidOn: alreadyPaid ? paidOn : null,
        dueOn: !alreadyPaid && dueOn ? dueOn : null,
        reference: reference.trim() || null,
        note: note.trim() || null,
        markOrderPaid: alreadyPaid && linkable && markPaid,
        payInvoiceExpenses: payingSupplies,
      }
      // The schema the API validates against, so the message is the server's.
      setFieldErrors({})
      const parsed = createOutgoingPaymentSchema.safeParse(body)
      if (!parsed.success) {
        const found: Record<string, string> = {}
        for (const issue of parsed.error.issues) {
          const path = String(issue.path[0] ?? '')
          const message =
            path === 'amountCents' && issue.code === 'invalid_type'
              ? 'Enter the amount as a number, e.g. 3749.50'
              : issue.message
          found[path] ??= message
          // "A paid entry needs both" is about the method and the date together.
          if (path === 'method') found.paidOn ??= message
        }
        setFieldErrors(found)
        throw new Error(Object.values(found)[0] ?? 'Check the highlighted fields and try again')
      }

      const created = await api.post<OutgoingPayment & { alsoPaid?: string[] }>(
        '/outgoing-payments',
        parsed.data,
      )
      if (file) {
        try {
          const form = new FormData()
          form.append('file', file)
          await api.postFile(`/outgoing-payments/${created.id}/document`, form)
        } catch (error) {
          toast.error(
            'Payment recorded, but the receipt could not be attached',
            error instanceof ApiError ? error.message : 'Open the payment and attach it again.',
          )
        }
      }
      return created
    },
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.expenses.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all() })
      await queryClient.invalidateQueries({ queryKey: queryKeys.purchasing.all() })
      toast.success(
        `${created.number} recorded`,
        created.alsoPaid?.length
          ? `Also paid the office supplies (${created.alsoPaid.join(', ')}).`
          : undefined,
      )
      onClose()
    },
    onError: (error: Error) => {
      setFormError(error.message)
      toast.error('Could not record the payment', error.message)
    },
  })

  const pickFile = (picked: File | undefined) => {
    if (!picked) return setFile(null)
    if (picked.size > MAX_COST_DOCUMENT_BYTES) {
      setFormError('That file is larger than the 10 MB attachment limit')
      return
    }
    setFormError(null)
    setFile(picked)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record a payment</DialogTitle>
        </DialogHeader>
        <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
          <Field label="Paying">
            {(props) => (
              <Select value={kind} onValueChange={(v) => setKind(v as OutgoingPaymentKind)}>
                <SelectTrigger {...props}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OUTGOING_PAYMENT_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {k === 'expense'
                        ? 'An expense (rent, printing, web design…)'
                        : OUTGOING_PAYMENT_KIND_LABELS[k]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>

          {kind === 'vendor' ? (
            <PickOne
              label="Vendor"
              items={lookups.vendors}
              value={vendorId}
              onChange={(v) => {
                setVendorId(v)
                setOrderId('')
                setPaySupplies(false)
                suppliesOfferedFor.current = ''
              }}
              name={(v) => v.name}
              error={fieldErrors.vendorId}
              placeholder="Choose a vendor"
            />
          ) : null}
          {kind === 'staff' ? (
            <PickOne
              label="Staff member"
              items={lookups.staff}
              value={staffUserId}
              onChange={setStaffUserId}
              name={(s) => s.name}
              error={fieldErrors.staffUserId}
              placeholder="Choose a staff member"
            />
          ) : null}
          {kind === 'payee' ? (
            <PickOne
              label="Freelancer"
              items={lookups.payees}
              value={payeeId}
              onChange={setPayeeId}
              name={(p) => p.name}
              error={fieldErrors.payeeId}
              placeholder="Choose a freelancer (add them on the People tab)"
            />
          ) : null}
          {kind === 'expense' ? (
            <>
              <PickOne
                label="Category"
                items={lookups.categories}
                value={categoryId}
                onChange={setCategoryId}
                name={(c) => c.name}
                error={fieldErrors.categoryId}
                placeholder="Choose a category"
              />
              <Field
                label="Paid to"
                required
                hint="e.g. Landlord, Print shop."
                error={fieldErrors.payeeLabel}
              >
                {(props) => (
                  <Input
                    {...props}
                    invalid={Boolean(fieldErrors.payeeLabel)}
                    value={payeeLabel}
                    onChange={(e) => setPayeeLabel(e.target.value)}
                  />
                )}
              </Field>
            </>
          ) : null}

          {kind === 'vendor' && vendor ? (
            <div className="space-y-3 rounded-md border border-border p-3">
              <Field
                label="For purchase order"
                hint="Optional."
                error={fieldErrors.purchaseOrderId}
              >
                {(props) => (
                  <Select
                    value={orderId || NONE}
                    onValueChange={(v) => {
                      setOrderId(v === NONE ? '' : v)
                      setMarkPaid(v !== NONE)
                      setPaySupplies(false)
                      suppliesOfferedFor.current = ''
                    }}
                  >
                    <SelectTrigger {...props} invalid={Boolean(fieldErrors.purchaseOrderId)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Not tied to an order</SelectItem>
                      {vendorOrders.map((o) => (
                        <SelectItem key={o.id} value={o.id}>
                          {o.poNumber}
                          {o.invoiceRef ? ` · ${o.invoiceRef}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              {!orderId ? (
                <Field
                  label="Or Purchasing invoice number"
                  hint="Marks those lines paid."
                  error={fieldErrors.invoiceRef}
                >
                  {(props) => (
                    <Input
                      {...props}
                      invalid={Boolean(fieldErrors.invoiceRef)}
                      value={invoiceRef}
                      onChange={(e) => setInvoiceRef(e.target.value)}
                    />
                  )}
                </Field>
              ) : null}
              {balance.data && orderId ? (
                <p className="text-sm text-foreground-muted">
                  {balance.data.poNumber} totals {formatMoney(balance.data.totalCents)} with
                  shipping and tax; {formatMoney(balance.data.paidCents)} already paid —{' '}
                  <span className="font-medium text-foreground">
                    {formatMoney(balance.data.balanceCents)} still owed
                  </span>
                  .
                </p>
              ) : null}
              {alreadyPaid && orderId && suppliesCents > 0 ? (
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={paySupplies}
                      onCheckedChange={toggleSupplies}
                      aria-label="Also pay the office supplies on this invoice"
                    />
                    Also pay the office supplies on this invoice (
                    {unpaidSupplies.map((e) => e.number).join(', ')}, {formatMoney(suppliesCents)})
                  </div>
                  {payingSupplies && amountCents > suppliesCents ? (
                    <p className="text-xs text-foreground-muted">
                      {formatMoney(amountCents - suppliesCents)} goes to {balance.data?.poNumber}{' '}
                      and {formatMoney(suppliesCents)} to office supplies.
                    </p>
                  ) : null}
                </div>
              ) : null}
              {alreadyPaid && linkable ? (
                <div className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={markPaid}
                    onCheckedChange={setMarkPaid}
                    aria-label="Mark the linked purchase paid"
                  />
                  Mark {orderId ? 'the order and its cost lines' : 'those purchase lines'} paid
                  {orderId && balance.data && !covers && markPaid ? (
                    <span className="text-xs text-warning">(this doesn't cover the balance)</span>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Amount" required hint="In dollars." error={fieldErrors.amountCents}>
              {(props) => (
                <Input
                  {...props}
                  invalid={Boolean(fieldErrors.amountCents)}
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value)
                    setFieldErrors(({ amountCents: _cleared, ...rest }) => rest)
                  }}
                />
              )}
            </Field>
            <div className="flex items-end pb-2">
              <div className="flex items-center gap-2 text-sm">
                <Switch
                  checked={alreadyPaid}
                  onCheckedChange={setAlreadyPaid}
                  aria-label="Already paid"
                />
                Already paid
              </div>
            </div>
          </div>

          {alreadyPaid ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Method" required error={fieldErrors.method}>
                {(props) => (
                  <Select
                    value={method}
                    onValueChange={(v) => setMethod(v as OutgoingPaymentMethod)}
                  >
                    <SelectTrigger {...props} invalid={Boolean(fieldErrors.method)}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MANUAL_PAYMENT_METHODS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {OUTGOING_PAYMENT_METHOD_LABELS[m]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Date paid" required error={fieldErrors.paidOn}>
                {(props) => (
                  <Input
                    {...props}
                    invalid={Boolean(fieldErrors.paidOn)}
                    type="date"
                    value={paidOn}
                    onChange={(e) => setPaidOn(e.target.value)}
                  />
                )}
              </Field>
            </div>
          ) : (
            <Field label="Due on" hint="Optional." error={fieldErrors.dueOn}>
              {(props) => (
                <Input
                  {...props}
                  invalid={Boolean(fieldErrors.dueOn)}
                  type="date"
                  value={dueOn}
                  onChange={(e) => setDueOn(e.target.value)}
                />
              )}
            </Field>
          )}

          <Field
            label="Reference"
            hint="Check number, Zelle confirmation, ACH trace…"
            error={fieldErrors.reference}
          >
            {(props) => (
              <Input
                {...props}
                invalid={Boolean(fieldErrors.reference)}
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            )}
          </Field>
          <Field label="Note" error={fieldErrors.note}>
            {(props) => (
              <Textarea
                {...props}
                invalid={Boolean(fieldErrors.note)}
                rows={2}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            )}
          </Field>
          <Field label="Receipt" hint="Optional PDF or image, up to 10 MB.">
            {(props) => (
              <Input
                {...props}
                type="file"
                accept={ACCEPT}
                onChange={(e) => pickFile(e.target.files?.[0])}
              />
            )}
          </Field>

          {formError ? (
            <p role="alert" className="text-sm text-danger">
              {formError}
            </p>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!(amountCents > 0)}
            onClick={() => save.mutate()}
          >
            Record payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
